import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { PageHeader } from "../components/PageHeader";
import { ExtractButton } from "../components/ExtractButton";
import { Plus, MagnifyingGlass, PencilSimple, Trash, UserGear, Ruler, Compass, Buildings, IdentificationBadge, SquaresFour, List as ListIcon, Phone, MapPin, User, Briefcase, EnvelopeSimple } from "@phosphor-icons/react";

const emptyForm = {
  nome: "", cognome: "", qualifica: "geometra", ordine: "", numero_iscrizione: "", provincia_albo: "",
  codice_fiscale: "", partita_iva: "",
  email: "", pec: "", telefono: "",
  indirizzo: "", citta: "", cap: "",
  ruolo_tipico: "", note: "",
};

const QUALIFICA_META = {
  geometra: { label: "Geometra", icon: Ruler },
  ingegnere: { label: "Ingegnere", icon: Compass },
  architetto: { label: "Architetto", icon: Buildings },
  perito: { label: "Perito", icon: IdentificationBadge },
  altro: { label: "Altro", icon: UserGear },
};

export default function Tecnici() {
  const [items, setItems] = useState([]);
  const [pratiche, setPratiche] = useState([]);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("tutti");
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("tecnici.view") || "cards");
  const changeViewMode = (m) => { setViewMode(m); try { localStorage.setItem("tecnici.view", m); } catch { /* ignore */ } };
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    const { data } = await api.get("/tecnici");
    setItems(data);
    try { const r = await api.get("/pratiche"); setPratiche(r.data || []); } catch { /* ignore */ }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ ...emptyForm, ...Object.fromEntries(Object.keys(emptyForm).map((k) => [k, c[k] || ""])) });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim()) { toast.error("Nome richiesto"); return; }
    try {
      if (editing) {
        await api.put(`/tecnici/${editing.id}`, form);
        toast.success("Tecnico aggiornato");
      } else {
        await api.post("/tecnici", form);
        toast.success("Tecnico creato");
      }
      setOpen(false);
      load();
    } catch { toast.error("Errore salvataggio"); }
  };

  const del = async (c) => {
    if (!window.confirm(`Eliminare il tecnico "${c.nome} ${c.cognome}"?`)) return;
    try {
      await api.delete(`/tecnici/${c.id}`);
      toast.success("Tecnico eliminato");
      load();
    } catch { toast.error("Errore"); }
  };

  const filtered = items.filter((c) => {
    if (filter !== "tutti" && c.qualifica !== filter) return false;
    if (!query) return true;
    const q = query.toLowerCase();
    return `${c.nome} ${c.cognome} ${c.ordine} ${c.numero_iscrizione} ${c.provincia_albo} ${c.email} ${c.telefono}`.toLowerCase().includes(q);
  });

  const qualifiche = Object.keys(QUALIFICA_META);

  return (
    <div className="p-4 md:p-8 w-full max-w-[1400px] mx-auto" data-testid="tecnici-page">
      <PageHeader
        eyebrow="Anagrafica"
        title="Tecnici colleghi"
        description="Rubrica di ingegneri, architetti e colleghi geometri con cui collabori. Con estrazione dal biglietto da visita o certificato d'albo."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNew} data-testid="new-tecnico-button"><Plus size={16} weight="bold" /> Nuovo tecnico</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editing ? "Modifica tecnico" : "Nuovo tecnico"}</DialogTitle></DialogHeader>
              {!editing && (
                <div className="mb-3 p-3 rounded-md bg-slate-50 border border-slate-200 flex items-center justify-between gap-3">
                  <div className="text-xs text-slate-600">
                    Carica biglietto da visita, timbro o certificato d&apos;albo: l&apos;AI compila i campi.
                  </div>
                  <ExtractButton
                    endpoint="tecnico"
                    testid="extract-tecnico-button"
                    onExtracted={(d) => {
                      setForm((prev) => ({
                        ...prev,
                        ...Object.fromEntries(Object.keys(emptyForm).map((k) => [k, d[k] || prev[k]])),
                      }));
                    }}
                  />
                </div>
              )}
              <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>Nome *</Label><Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} required data-testid="tecnico-nome-input" /></div>
                  <div className="space-y-2"><Label>Cognome</Label><Input value={form.cognome} onChange={(e) => setForm({ ...form, cognome: e.target.value })} /></div>
                </div>
                <div className="space-y-2">
                  <Label>Qualifica</Label>
                  <Select value={form.qualifica} onValueChange={(v) => setForm({ ...form, qualifica: v })}>
                    <SelectTrigger data-testid="tecnico-qualifica-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {qualifiche.map((k) => (
                        <SelectItem key={k} value={k}>{QUALIFICA_META[k].label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2 space-y-2"><Label>Ordine / Collegio</Label><Input value={form.ordine} onChange={(e) => setForm({ ...form, ordine: e.target.value })} placeholder="Es. Collegio Geometri di..." /></div>
                  <div className="space-y-2"><Label>Provincia albo</Label><Input value={form.provincia_albo} onChange={(e) => setForm({ ...form, provincia_albo: e.target.value.toUpperCase() })} maxLength={2} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>N. iscrizione</Label><Input value={form.numero_iscrizione} onChange={(e) => setForm({ ...form, numero_iscrizione: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Ruolo tipico</Label><Input value={form.ruolo_tipico} onChange={(e) => setForm({ ...form, ruolo_tipico: e.target.value })} placeholder="Es. Direttore Lavori" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>Codice Fiscale</Label><Input value={form.codice_fiscale} onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value.toUpperCase() })} /></div>
                  <div className="space-y-2"><Label>Partita IVA</Label><Input value={form.partita_iva} onChange={(e) => setForm({ ...form, partita_iva: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                  <div className="space-y-2"><Label>PEC</Label><Input type="email" value={form.pec} onChange={(e) => setForm({ ...form, pec: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>Telefono</Label><Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} /></div>
                  <div className="space-y-2"><Label>CAP</Label><Input value={form.cap} onChange={(e) => setForm({ ...form, cap: e.target.value })} /></div>
                </div>
                <div className="space-y-2"><Label>Indirizzo studio</Label><Input value={form.indirizzo} onChange={(e) => setForm({ ...form, indirizzo: e.target.value })} /></div>
                <div className="space-y-2"><Label>Città</Label><Input value={form.citta} onChange={(e) => setForm({ ...form, citta: e.target.value })} /></div>
                <div className="space-y-2"><Label>Note</Label><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} /></div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
                  <Button type="submit" data-testid="tecnico-submit-button">Salva</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cerca tecnico per cognome, ordine, email..." className="pl-9" data-testid="tecnici-search-input" />
        </div>
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-md p-1">
          <button onClick={() => setFilter("tutti")} className={`px-3 py-1 text-xs rounded ${filter === "tutti" ? "bg-slate-900 text-white" : "text-slate-600"}`}>Tutti</button>
          {qualifiche.map((k) => (
            <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1 text-xs rounded ${filter === k ? "bg-slate-900 text-white" : "text-slate-600"}`}>{QUALIFICA_META[k].label}</button>
          ))}
        </div>
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-md p-1" data-testid="view-toggle">
          <button onClick={() => changeViewMode("cards")} data-testid="view-cards" className={`px-2.5 py-1.5 rounded text-xs font-medium inline-flex items-center gap-1.5 ${viewMode === "cards" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}><SquaresFour size={14} /> Schede</button>
          <button onClick={() => changeViewMode("list")} data-testid="view-list" className={`px-2.5 py-1.5 rounded text-xs font-medium inline-flex items-center gap-1.5 ${viewMode === "list" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}><ListIcon size={14} /> Lista</button>
        </div>
        <div className="text-xs font-mono text-slate-500 ml-auto">{filtered.length} tecnici</div>
      </div>

      {viewMode === "cards" ? (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map((c) => {
          const qm = QUALIFICA_META[c.qualifica] || QUALIFICA_META.altro;
          const QIcon = qm.icon;
          const tecPratiche = pratiche.filter((p) => (p.tecnici_ids || []).includes(c.id));
          const attive = tecPratiche.filter((p) => p.stato !== "completata" && p.stato !== "annullata").length;
          const completate = tecPratiche.filter((p) => p.stato === "completata").length;
          return (
            <Card key={c.id} className="p-5 border-slate-200 shadow-none hover:border-slate-300 hover:shadow-md transition-all" data-testid={`tecnico-card-${c.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="h-9 w-9 rounded-md bg-slate-100 flex items-center justify-center"><QIcon size={18} className="text-slate-700" /></div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`edit-tecnico-${c.id}`}><PencilSimple size={14} /></Button>
                  <Button variant="ghost" size="sm" onClick={() => del(c)}><Trash size={14} className="text-red-600" /></Button>
                </div>
              </div>
              <div className="font-heading font-semibold text-slate-900 truncate">{`${c.cognome || ""} ${c.nome || ""}`.trim() || "—"}</div>
              <div className="text-[10px] text-slate-500 mb-3 font-mono uppercase tracking-widest">{qm.label}</div>
              <div className="space-y-1 text-xs text-slate-600">
                {c.numero_iscrizione && <div className="flex items-center gap-1.5 truncate"><IdentificationBadge size={12} className="text-slate-400 flex-shrink-0" /><span className="truncate">N° {c.numero_iscrizione}{c.provincia_albo && ` — ${c.provincia_albo}`}</span></div>}
                {c.ordine && <div className="truncate text-[11px]">{c.ordine}</div>}
                {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-1.5 truncate hover:text-blue-600" onClick={(e) => e.stopPropagation()}><EnvelopeSimple size={12} className="text-slate-400 flex-shrink-0" /><span className="truncate">{c.email}</span></a>}
                {c.telefono && <a href={`tel:${c.telefono}`} className="flex items-center gap-1.5 truncate font-mono hover:text-blue-600" onClick={(e) => e.stopPropagation()}><Phone size={12} className="text-slate-400 flex-shrink-0" /><span className="truncate">{c.telefono}</span></a>}
                {c.ruolo_tipico && <div className="truncate italic text-[11px]">▸ {c.ruolo_tipico}</div>}
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2">
                <Briefcase size={12} className="text-slate-400" />
                <span className="text-[10px] font-mono uppercase tracking-wider text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">Attive: {attive}</span>
                <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">Completate: {completate}</span>
              </div>
            </Card>
          );
        })}
        {filtered.length === 0 && (
          <div className="col-span-full p-12 text-center text-sm text-slate-500">
            Nessun tecnico trovato. <Button variant="link" onClick={openNew}>Aggiungi il primo</Button>
          </div>
        )}
      </div>
      ) : (
        <Card className="border-slate-200 shadow-none overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200"><tr className="text-left">
                <th className="p-3 font-mono text-[10px] uppercase tracking-widest text-slate-600">Tecnico</th>
                <th className="p-3 font-mono text-[10px] uppercase tracking-widest text-slate-600">Ordine / N°</th>
                <th className="p-3 font-mono text-[10px] uppercase tracking-widest text-slate-600">Email</th>
                <th className="p-3 font-mono text-[10px] uppercase tracking-widest text-slate-600">Telefono</th>
                <th className="p-3 font-mono text-[10px] uppercase tracking-widest text-slate-600 text-center">Pratiche</th>
                <th className="p-3" />
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((c) => {
                  const qm = QUALIFICA_META[c.qualifica] || QUALIFICA_META.altro;
                  const tecPratiche = pratiche.filter((p) => (p.tecnici_ids || []).includes(c.id));
                  const attive = tecPratiche.filter((p) => p.stato !== "completata" && p.stato !== "annullata").length;
                  const completate = tecPratiche.filter((p) => p.stato === "completata").length;
                  return (
                    <tr key={c.id} className="hover:bg-slate-50" data-testid={`tecnico-row-${c.id}`}>
                      <td className="p-3"><div className="font-semibold text-slate-900">{`${c.cognome || ""} ${c.nome || ""}`.trim() || "—"}</div><div className="text-[10px] font-mono uppercase text-slate-500">{qm.label}</div></td>
                      <td className="p-3 text-xs text-slate-600">{c.ordine || "—"}{c.numero_iscrizione ? ` · N°${c.numero_iscrizione}` : ""}</td>
                      <td className="p-3 text-xs text-slate-600 truncate max-w-[220px]">{c.email || "—"}</td>
                      <td className="p-3 text-xs font-mono text-slate-600">{c.telefono || "—"}</td>
                      <td className="p-3 text-center"><div className="inline-flex gap-1"><span className="text-[10px] font-mono uppercase text-amber-800 bg-amber-50 border border-amber-200 rounded px-1 py-0.5">A: {attive}</span><span className="text-[10px] font-mono uppercase text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-1 py-0.5">C: {completate}</span></div></td>
                      <td className="p-3 text-right whitespace-nowrap"><Button variant="ghost" size="sm" onClick={() => openEdit(c)}><PencilSimple size={14} /></Button><Button variant="ghost" size="sm" onClick={() => del(c)}><Trash size={14} className="text-red-600" /></Button></td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && <tr><td colSpan={6} className="p-12 text-center text-sm text-slate-500">Nessun tecnico</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
